import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';
import { membershipPlanKeys } from '@/lib/queryKeys';

export type RecurrenceType = 'WEEKLY' | 'MONTHLY' | 'SEASONAL' | 'DAILY';
export type DurationType = 'CONTINUOUS' | 'FIXED';

/** A plan's included-visit config row (membership_plan_recurring_services). */
export interface PlanRecurringService {
  id: string;
  planId: string;
  label: string | null;
  jobTypeId: string;
  businessUnitId: string | null;
  invoiceTemplateItemId: string | null;
  priority: string | null;
  jobSummary: string | null;
  recurrenceType: RecurrenceType;
  recurrenceInterval: number;
  recurrenceMonths: number[];
  durationType: DurationType;
  totalVisits: number | null;
  firstVisitComplete: boolean;
  coverageId: string | null;
  attachTo: 'PROPERTY' | 'UNIT';
  scaling: 'EXTEND' | 'MULTIPLY';
  extraUnitMinutes: number;
}

export interface PlanRecurringServiceInput {
  id?: string;
  label?: string | null;
  jobTypeId: string;
  businessUnitId?: string | null;
  invoiceTemplateItemId?: string | null;
  priority?: string | null;
  jobSummary?: string | null;
  recurrenceType: RecurrenceType;
  recurrenceInterval?: number;
  recurrenceMonths?: number[];
  durationType: DurationType;
  totalVisits?: number | null;
  firstVisitComplete?: boolean;
  coverageId?: string | null;
  attachTo?: 'PROPERTY' | 'UNIT';
  scaling?: 'EXTEND' | 'MULTIPLY';
  extraUnitMinutes?: number;
}

type Envelope<T> = { data: T };

export function usePlanRecurringServices(planId: string | null) {
  return useApiQuery<PlanRecurringService[]>({
    queryKey: membershipPlanKeys.recurringServices(planId ?? 'none'),
    enabled: Boolean(planId),
    queryFn: async () => {
      const res = await apiGet<Envelope<PlanRecurringService[]>>(
        operationsClient,
        operationsPricebookEndpoints.planRecurringServices(planId!),
      );
      return res.data;
    },
  });
}

function useInvalidate(planId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: membershipPlanKeys.recurringServices(planId),
    });
}

export function useCreatePlanRecurringService(planId: string) {
  const invalidate = useInvalidate(planId);
  return useApiMutation<PlanRecurringService, PlanRecurringServiceInput>({
    mutationFn: async (input) => {
      const res = await apiPost<Envelope<PlanRecurringService>, PlanRecurringServiceInput>(
        operationsClient,
        operationsPricebookEndpoints.planRecurringServices(planId),
        input,
      );
      return res.data;
    },
    onSuccess: () => void invalidate(),
  });
}

export function useUpdatePlanRecurringService(planId: string) {
  const invalidate = useInvalidate(planId);
  return useApiMutation<
    PlanRecurringService,
    { id: string; input: Partial<PlanRecurringServiceInput> }
  >({
    mutationFn: async ({ id, input }) => {
      const res = await apiPatch<
        Envelope<PlanRecurringService>,
        Partial<PlanRecurringServiceInput>
      >(operationsClient, operationsPricebookEndpoints.planRecurringService(planId, id), input);
      return res.data;
    },
    onSuccess: () => void invalidate(),
  });
}

export function useDeletePlanRecurringService(planId: string) {
  const invalidate = useInvalidate(planId);
  return useApiMutation<void, string>({
    mutationFn: async (id) => {
      await apiDelete(
        operationsClient,
        operationsPricebookEndpoints.planRecurringService(planId, id),
      );
    },
    onSuccess: () => void invalidate(),
  });
}
